import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ClipboardList, Plus, X, RotateCcw, Save } from "lucide-react";

const LABELS = {
  tractor: "Tractor", mower: "Mower", excavator: "Excavator", vehicle: "Vehicle",
  loader: "Loader", harvester: "Harvester", other: "Other",
};

export default function PreStartChecklistSettings() {
  const [data, setData] = useState(null);
  const [type, setType] = useState("tractor");
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/checklists").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!data) return;
    const c = data.checklists.find((x) => x.machine_type === type);
    setItems(c ? [...c.items] : []);
  }, [type, data]);

  const current = data?.checklists.find((x) => x.machine_type === type);

  const setItem = (i, val) => setItems((arr) => arr.map((x, idx) => (idx === i ? val : x)));
  const removeItem = (i) => setItems((arr) => arr.filter((_, idx) => idx !== i));
  const addItem = () => setItems((arr) => [...arr, ""]);

  const save = async () => {
    const clean = items.map((s) => s.trim()).filter(Boolean);
    setSaving(true);
    try {
      await api.put(`/checklists/${type}`, { items: clean });
      toast.success(`${LABELS[type]} checklist saved`);
      await load();
    } catch { toast.error("Could not save checklist"); }
    finally { setSaving(false); }
  };

  const reset = async () => {
    setSaving(true);
    try {
      await api.put(`/checklists/${type}`, { items: [] });
      toast.success(`${LABELS[type]} reset to default`);
      await load();
    } catch { toast.error("Could not reset checklist"); }
    finally { setSaving(false); }
  };

  if (!data) return null;

  return (
    <div className="mt-10" data-testid="checklist-settings-section">
      <div className="flex items-center gap-3 mb-3">
        <ClipboardList className="w-5 h-5 text-[#FFB800]" />
        <h2 className="font-head font-900 uppercase tracking-tight text-xl">Pre-Start Checklists</h2>
      </div>
      <p className="font-body text-sm text-[#94A3B8] mb-4 max-w-lg">
        Customise the pre-start checklist operators see when they scan a machine&apos;s QR code. Set one per machine type.
      </p>

      <div className="flex flex-wrap gap-1.5 mb-4" data-testid="checklist-type-tabs">
        {data.checklists.map((c) => (
          <button key={c.machine_type} onClick={() => setType(c.machine_type)}
            className={`px-3 h-9 font-head text-xs uppercase tracking-wide transition-colors border ${type === c.machine_type ? "bg-[#FFB800] text-black border-[#FFB800]" : "border-white/15 text-[#94A3B8] hover:text-white"}`}
            data-testid={`checklist-tab-${c.machine_type}`}>
            {LABELS[c.machine_type]}{c.is_custom ? " •" : ""}
          </button>
        ))}
      </div>

      <div className="card p-5">
        <div className="flex items-center justify-between mb-3">
          <p className="font-mono text-[10px] uppercase tracking-widest text-[#94A3B8]">
            {LABELS[type]} — {current?.is_custom ? "Custom" : "Default"} ({items.length} items)
          </p>
        </div>
        <div className="space-y-2">
          {items.map((it, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`checklist-item-${i}`}>
              <span className="font-mono text-xs text-[#94A3B8] w-6 shrink-0">{i + 1}.</span>
              <input className="il-input flex-1" value={it} onChange={(e) => setItem(i, e.target.value)} placeholder="Checklist item" data-testid={`checklist-input-${i}`} />
              <button onClick={() => removeItem(i)} className="text-[#94A3B8] hover:text-[#FF3B30] transition-colors p-2" data-testid={`checklist-remove-${i}`}><X className="w-4 h-4" /></button>
            </div>
          ))}
          {items.length === 0 && <p className="font-body text-sm text-[#94A3B8]">No items — add some below.</p>}
        </div>
        <button onClick={addItem} className="mt-3 flex items-center gap-1.5 font-head text-xs uppercase tracking-wide text-[#FFB800] hover:brightness-110 transition-all" data-testid="checklist-add-item">
          <Plus className="w-4 h-4" /> Add item
        </button>

        <div className="flex flex-col sm:flex-row gap-3 mt-5 pt-4 border-t border-white/10">
          <button onClick={save} disabled={saving} className="btn-signal h-11 px-5 font-head text-sm flex items-center justify-center gap-2 flex-1 disabled:opacity-50" data-testid="checklist-save-btn">
            <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save Checklist"}
          </button>
          <button onClick={reset} disabled={saving || !current?.is_custom} className="btn-ghost h-11 px-5 font-head text-sm flex items-center justify-center gap-2 disabled:opacity-40" data-testid="checklist-reset-btn">
            <RotateCcw className="w-4 h-4" /> Reset to Default
          </button>
        </div>
      </div>
    </div>
  );
}
